import React, { useState, useContext, useMemo } from "react";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaPlus, FaSave } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";

const MakeCapacityMasterEdit = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();
  const location = useLocation();

  const editId = useMemo(
    () => new URLSearchParams(location.search).get("id"),
    [location.search]
  );
  const isEdit = !!editId;

  const [formData, setFormData] = useState({
    makeName: "30 ft",
    makeCode: "Ma001",
    remarks: "",
    isActive: true,
  });

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const requiredClass = "text-red-500";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = () => {
    // Validation
    if (!formData.makeName.trim()) {
      toast.error("Make Name is required!");
      return;
    }
    if (!formData.makeCode.trim()) {
      toast.error("Make Code is required!");
      return;
    }

    // Save logic here
    console.log("Saving make/capacity:", formData);
    toast.success("Make/Capacity saved successfully!");
  };

  const handleAddNew = () => {
    // Reset form for new entry
    setFormData({
      makeName: "",
      makeCode: "MAK/247",
      remarks: "",
      isActive: true,
    });
    toast.info("Form reset for new entry");
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">MAKE /CAPACITY MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white text-black`
        }`}>
          {/* Header Section */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              
              
              <h1 className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}>
                Make /Capacity Master
              </h1>
            </div>
            <div className="flex items-end"><button
                onClick={handleBack}
                 className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark 
                        ? `${buttonClass} ${hoverClass} text-white` 
                        : `${buttonClass} ${hoverClass} text-white`
                    }`}
              >
                <FaArrowLeft />
                Back
              </button></div>
            
           
          </div>

          {/* Form Section */}
          <div className={`rounded-xl border p-6 ${
            isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
          }`}>
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Make Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Make Name <span className={requiredClass}>*</span>
                </label>
                <input
                  type="text"
                  name="makeName"
                  value={formData.makeName}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter make name"
                />
              </div>

              {/* Make Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Make Code
                </label>
                <input
                  type="text"
                  name="makeCode"
                  value={formData.makeCode}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter make code"
                />
              </div>

              {/* Remarks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <label className={`${labelClass} text-right md:text-right pt-2`}>
                  Remarks
                </label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  rows={4}
                  className={inputClass}
                  placeholder="Enter remarks"
                />
              </div>

              {/* IsActive Checkbox */}
              <div className="border-t pt-6 mt-6 border-gray-200 dark:border-gray-700">
                <div className="flex justify-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      IsActive
                    </span>
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSave}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark 
                      ? "bg-blue-700 hover:bg-blue-600 text-white" 
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  <FaSave />
                  Save
                </button>

                <button
                  onClick={handleAddNew}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark 
                        ? `${buttonClass} ${hoverClass} text-white` 
                        : `${buttonClass} ${hoverClass} text-white`
                    }`}
                >
                  <FaPlus />
                  Add New
                </button>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default MakeCapacityMasterEdit;